import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  UnfoldMoreRounded,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { clientMoneyUnavailable, dspLabel, num, withMargin } from '../format';
import { useResizableColumns } from '../hooks/useResizableColumns';
import type { PublisherInventoryRow } from '../lib/publisherInventory';
import { EXPORT_SECTIONS, buildStackedCsv, downloadFile } from '../lib/reportExport';
import { COLORS } from '../theme';
import ColumnResizeHandle from './ColumnResizeHandle';
import { ROWS_PER_PAGE_OPTIONS, paginateRows } from './Conversions';
import { COLS, columnNote, isGated, isSortGated, pageWindow, visibleColumns } from './ImpressionsByType';
import WidgetExportButton from './WidgetExportButton';
import WidgetSkeleton from './WidgetSkeleton';

interface IProps {
  rows: PublisherInventoryRow[];
  loading: boolean;
  error: string | null;
  restrictedFields: string[];
  truncated: boolean;
  /** Expanded-row app/DSP breakdown hit its own row cap — distinct from `truncated`, which is about the table's own totals (Lucas, review). */
  appDetailsTruncated: boolean;
  /** Publisher directory or appDetails failed — only the expanded row's app/DSP breakdown is affected, never the top-level rows (review, CodeRabbit). */
  appDetailsError: string | null;
  canViewPlatform: boolean;
  /** Raw inventory (app_name) being gated off is a DIFFERENT permission from canViewPlatform (Lucas, review) — an expanded row needs both. */
  canViewRawInventory: boolean;
  canViewWholesaleSpend: boolean;
  /** flank#4745 — whether the CURRENT selection has any priced impressions at
   * all, distinct from canViewClientSpend (the permission). See
   * useReportingPermissions.deriveHasClientSpendData. */
  hasClientSpendData: boolean;
  /** Already folds in entityReach — see isGated/isSortGated below; no separate use for the raw per-platform flag here. */
  entityReach: boolean;
  reachColumn: boolean;
  perPlatformConversions: boolean;
}

const NAME_SORT = '__name' as const;
type SortCol = (typeof COLS)[number]['key'] | typeof NAME_SORT;

// Same 20/50/100 toggle styling Conversions.tsx, ImpressionsByType.tsx and
// ConversionsBreakdown.tsx each define locally (prep #0000) — not extracted,
// matching how those already keep their own copies rather than sharing one.
const darkToggleSx = {
  bgcolor: COLORS.pageBg,
  borderRadius: '10px',
  border: `1px solid ${COLORS.border}`,
  p: 0.5,
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    border: 0,
    borderRadius: '8px !important',
    px: 1.5,
    py: 0.25,
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 600,
    '&.Mui-selected': { bgcolor: COLORS.black, color: '#fff', '&:hover': { bgcolor: '#000' } },
  },
} as const;

const KIND_LABEL: Record<PublisherInventoryRow['kind'], string> = {
  publisher: 'Publisher',
  unmapped: 'Unmapped',
  unknown: 'Unknown',
};

const cell = (value: unknown, fmt: (v: unknown) => string) =>
  value === null || value === undefined || value === '' ? '—' : fmt(value);

const SortIcon = ({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol; sortDir: 'asc' | 'desc' }) => {
  if (col !== sortCol)
    return <UnfoldMoreRounded sx={{ fontSize: 14, color: COLORS.border, verticalAlign: 'middle' }} />;
  return sortDir === 'desc' ? (
    <ArrowDownwardRounded sx={{ fontSize: 14, color: COLORS.primary, verticalAlign: 'middle' }} />
  ) : (
    <ArrowUpwardRounded sx={{ fontSize: 14, color: COLORS.primary, verticalAlign: 'middle' }} />
  );
};

/**
 * CSV export for the Publishers view (flank#4643) — a rollup, not the raw
 * per-dimension rows `useExportWidgetCsv`/EXPORT_SECTIONS read, so it can't
 * reuse that path. Reuses the same CSV builders (injection-safe cells,
 * stacked sections) as every other export in this page; the expanded app/DSP
 * detail isn't included — the row-level totals are what has to match legacy.
 */
/**
 * Writes `null` for a gated column instead of its real value (Lucas, review):
 * the screen already renders "—" for these via `isGated` in the cell loop
 * below, but the export previously carried the raw figure regardless, so a
 * CSV could leak reach/frequency/conversions the screen itself withholds.
 */
const exportCsv = (
  rows: PublisherInventoryRow[],
  visibleCols: typeof COLS,
  reachColumn: boolean,
  perPlatformConversions: boolean,
  truncated: boolean,
) => {
  const section = EXPORT_SECTIONS.find(s => s.key === 'impressionsByType');
  const csvRows: ReportRow[] = rows.map(row => {
    const out: ReportRow = {
      name: row.name,
      type: KIND_LABEL[row.kind],
      apps_mapped: row.appCount ?? 1,
    };
    for (const col of visibleCols) {
      out[col.key] = isGated(col, reachColumn, perPlatformConversions) ? null : (row.metrics[col.key] ?? null);
    }
    return out;
  });
  // Same warning the on-screen table shows (review, CodeRabbit) — the export
  // has no real "of N rows" count to reuse the generic totalRows-based
  // truncationNote mechanism, so this is its own copy of that text.
  const meta = truncated
    ? [
        'This selection has more distinct apps than the report can fetch in one page — totals for the smallest ones may be undercounted. Narrow your filters to see the full picture.',
      ]
    : [];
  const csv = buildStackedCsv(
    [{ key: 'inventoryPublishers', label: section?.label ?? 'Impressions by Type', rows: csvRows }],
    meta,
  );
  downloadFile(csv, `inventory_publishers_${Date.now()}.csv`, 'text/csv');
};

/**
 * Impressions by Type -> Inventory -> Publishers (flank#4643). Each app
 * bundle mapped to a Ribeye Publisher rolls up into one row (logo + name from
 * Directus, "N apps mapped", expandable); an unmapped bundle keeps its own
 * row rather than disappearing; junk values with no usable name land in one
 * combined "Unknown / Pending mapping" row. Everything still counts toward
 * the column totals. Domains are a separate concern, not shown here — see
 * publisherInventory.ts's module doc comment.
 *
 * Columns, their formats and the reach/conversions/client-money gating are
 * the exact same ones the other Inventory chips use (`COLS`/`visibleColumns`
 * from ImpressionsByType) so this view reads consistently with the rest of
 * the tab (#4636's convention) rather than inventing its own column set.
 */
const PublisherInventoryTable = ({
  rows,
  loading,
  error,
  restrictedFields,
  truncated,
  appDetailsTruncated,
  appDetailsError,
  canViewPlatform,
  canViewRawInventory,
  canViewWholesaleSpend,
  hasClientSpendData,
  entityReach,
  reachColumn,
  perPlatformConversions,
}: IProps) => {
  const [sortCol, setSortCol] = useState<SortCol>('impressions');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const { widths: colWidths, startResize } = useResizableColumns('impressionsByType.inventory');
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Margin (#4447) is derived client-side from client_spend/spend, same as
  // every other Inventory chip — reusing `withMargin` rather than
  // re-deriving the formula here.
  const rowsWithMargin: PublisherInventoryRow[] = useMemo(
    () =>
      rows.map(row => ({
        ...row,
        metrics: withMargin([row.metrics as ReportRow])[0] as PublisherInventoryRow['metrics'],
      })),
    [rows],
  );

  // flank#4745 follow-up (preprod QA): clientMoneyUnavailable alone only catches a
  // row shape with no client_spend/client_cpm KEY at all — it missed the
  // Bonneville-style case where the key is present but every value is
  // unpriced for the current selection. hasClientSpendData is the same
  // priced-impressions signal TopCampaignsCard/TopPublishersCard already use.
  const hideClientMoney =
    clientMoneyUnavailable(rowsWithMargin.map(r => r.metrics as ReportRow)) || !hasClientSpendData;
  const visibleCols = visibleColumns(COLS, hideClientMoney, canViewWholesaleSpend, restrictedFields);

  // Same reset GroupedBreakdownCard applies (review, CodeRabbit): a sort
  // column that becomes hidden or gated (permission withdrawn, reach/
  // conversions no longer comparable) must not keep the rows ordered by a
  // column that no longer renders.
  useEffect(() => {
    if (sortCol === NAME_SORT) return;
    const col = visibleCols.find(c => c.key === sortCol);
    if (!col || isSortGated(col, reachColumn, perPlatformConversions, entityReach)) setSortCol('impressions');
  }, [visibleCols, sortCol, reachColumn, perPlatformConversions, entityReach]);

  const sortedRows = useMemo(() => {
    const out = [...rowsWithMargin];
    out.sort((a, b) => {
      if (sortCol === NAME_SORT) return sortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      const av = Number(a.metrics[sortCol] ?? 0);
      const bv = Number(b.metrics[sortCol] ?? 0);
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return out;
  }, [rowsWithMargin, sortCol, sortDir]);

  const { pageRows, pageCount, safePage } = paginateRows(sortedRows, page, rowsPerPage);

  const handleSort = (col: SortCol) => {
    if (col === sortCol) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortCol(col);
      setSortDir(col === NAME_SORT ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const handleRowsPerPageChange = (next: number) => {
    setRowsPerPage(next);
    setPage(0);
  };

  const toggleExpand = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (loading) return <WidgetSkeleton rows={20} />;
  if (error)
    return (
      <Typography variant='body2' color='error'>
        {error}
      </Typography>
    );
  if (rows.length === 0) {
    return (
      <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
        No rows for the selected filters.
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <WidgetExportButton
          onExport={() => exportCsv(sortedRows, visibleCols, reachColumn, perPlatformConversions, truncated)}
          disabled={rows.length === 0}
        />
      </Box>
      <Box className='rv2-scroll' sx={{ overflowX: 'auto' }}>
        <Table size='small' sx={{ tableLayout: 'fixed', minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  // Was 36% — disproportionately wide next to the numeric
                  // columns it was starving (flank#4643 QA follow-up to
                  // #4716); the name still gets its own ellipsis+tooltip below.
                  width: '20%',
                  color: COLORS.muted,
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { color: COLORS.text },
                }}
                onClick={() => handleSort(NAME_SORT)}
              >
                Publisher <SortIcon col={NAME_SORT} sortCol={sortCol} sortDir={sortDir} />
              </TableCell>
              {visibleCols.map(col => {
                const gated = isGated(col, reachColumn, perPlatformConversions);
                const unclickable =
                  col.sortable === false || isSortGated(col, reachColumn, perPlatformConversions, entityReach);
                const note = columnNote(col, gated, entityReach, canViewPlatform);
                return (
                  <TableCell
                    key={col.key}
                    align='right'
                    // Full label on hover (Lucas, #4716 review nit) — the
                    // column is often clipped to 2-4 characters by the
                    // ellipsis below with no other way to read the whole name.
                    title={col.label}
                    sx={{
                      position: 'relative',
                      width: colWidths[col.key] ? `${colWidths[col.key]}px` : col.width,
                      color: COLORS.muted,
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: unclickable ? 'default' : 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      // A right-aligned nowrap label wider than its fixed-%
                      // column (e.g. "Avg. Daily Freq.", "Client CPM")
                      // otherwise overflows leftward into the PRECEDING
                      // column's header instead of clipping at its own
                      // boundary — reported as overlapping headers on the
                      // Publishers tab (flank#4643 QA).
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      ...(unclickable ? {} : { '&:hover': { color: COLORS.text } }),
                    }}
                    onClick={unclickable ? undefined : () => handleSort(col.key)}
                  >
                    {note ? (
                      <Tooltip title={note} placement='top' arrow>
                        <span>{col.label}</span>
                      </Tooltip>
                    ) : (
                      col.label
                    )}{' '}
                    {unclickable ? null : <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />}
                    <ColumnResizeHandle columnKey={col.key} onResizeStart={startResize} />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map(row => {
              const isExpandable = row.kind === 'publisher' && (row.appCount ?? 0) > 0;
              const isExpanded = expanded.has(row.id);
              return (
                <Fragment key={row.id}>
                  <TableRow sx={{ '&:hover': { bgcolor: COLORS.pageBg } }}>
                    <TableCell sx={{ overflow: 'hidden' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        {isExpandable && (
                          <IconButton size='small' onClick={() => toggleExpand(row.id)} sx={{ p: 0.25, flexShrink: 0 }}>
                            {isExpanded ? (
                              <ExpandLessRounded fontSize='small' />
                            ) : (
                              <ExpandMoreRounded fontSize='small' />
                            )}
                          </IconButton>
                        )}
                        {row.kind === 'publisher' && (
                          <Avatar src={row.logo ?? undefined} sx={{ width: 24, height: 24, flexShrink: 0 }}>
                            {row.name.charAt(0)}
                          </Avatar>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography
                              component='span'
                              variant='body2'
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {row.name}
                            </Typography>
                            {row.kind === 'unmapped' && (
                              <Chip label='Unmapped' size='small' sx={{ height: 18, fontSize: 10 }} />
                            )}
                          </Box>
                          {row.kind === 'publisher' && (
                            <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block' }}>
                              {row.appCount} {row.appCount === 1 ? 'app' : 'apps'} mapped to this publisher
                            </Typography>
                          )}
                          {row.kind === 'unknown' && (
                            <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block' }}>
                              {row.appCount} {row.appCount === 1 ? 'item' : 'items'} with no usable name
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    {visibleCols.map(col => {
                      const gated = isGated(col, reachColumn, perPlatformConversions);
                      return (
                        <TableCell
                          key={col.key}
                          align='right'
                          // nowrap + the widened COLS widths (#4716 follow-up)
                          // keep a large formatted value from visually running
                          // into the next column ("9,072,5851.84" on preprod QA).
                          sx={{ fontWeight: col.key === sortCol ? 600 : 400, whiteSpace: 'nowrap' }}
                        >
                          {gated ? '—' : cell(row.metrics[col.key], col.format)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {isExpandable && isExpanded && (
                    <TableRow>
                      <TableCell colSpan={visibleCols.length + 1} sx={{ bgcolor: COLORS.pageBg, py: 1.5 }}>
                        {!canViewPlatform ? (
                          <Typography variant='caption' sx={{ color: COLORS.muted }}>
                            App-level detail (bundle IDs and DSP names) requires Platform access.
                          </Typography>
                        ) : !canViewRawInventory ? (
                          <Typography variant='caption' sx={{ color: COLORS.muted }}>
                            App detail requires raw inventory access.
                          </Typography>
                        ) : appDetailsError ? (
                          <Typography variant='caption' color='error'>
                            {appDetailsError}
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {(row.apps ?? []).map((app, i) => (
                              <Box
                                key={`${app.bundle}-${app.dsp ?? i}`}
                                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 12 }}
                              >
                                <Typography variant='caption' sx={{ color: COLORS.text }}>
                                  {app.bundle}
                                  {app.dsp ? ` — ${dspLabel(app.dsp)}` : ''}
                                </Typography>
                                <Typography variant='caption' sx={{ color: COLORS.muted }}>
                                  {cell(app.metrics.impressions, num)} impressions
                                </Typography>
                              </Box>
                            ))}
                            {appDetailsTruncated && (
                              <Typography variant='caption' sx={{ color: COLORS.muted, pt: 0.5 }}>
                                More apps than this breakdown can fetch in one page — the smallest may be missing.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {truncated && (
        <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block', pt: 1 }}>
          This selection has more distinct apps than the report can fetch in one page — totals for the smallest ones may
          be undercounted. Narrow your filters to see the full picture.
        </Typography>
      )}

      {sortedRows.length > ROWS_PER_PAGE_OPTIONS[0] && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            pt: 1,
          }}
        >
          <Typography variant='caption' sx={{ color: COLORS.muted }}>
            {(safePage * rowsPerPage + 1).toLocaleString()}–
            {(safePage * rowsPerPage + pageRows.length).toLocaleString()} of {sortedRows.length.toLocaleString()} rows
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant='caption' sx={{ color: COLORS.muted }}>
                Rows
              </Typography>
              <ToggleButtonGroup
                size='small'
                exclusive
                value={rowsPerPage}
                onChange={(_e, v) => v && handleRowsPerPageChange(v)}
                sx={darkToggleSx}
              >
                {ROWS_PER_PAGE_OPTIONS.map(n => (
                  <ToggleButton key={n} value={n}>
                    {n}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box className='rv2-noprint' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size='small'
                disabled={safePage === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
              >
                Prev
              </Button>
              {pageWindow(safePage, pageCount).map((n, i) =>
                n === -1 ? (
                  <Typography key={`gap-${i}`} variant='caption' sx={{ color: COLORS.muted, px: 0.5 }}>
                    …
                  </Typography>
                ) : (
                  <Button
                    key={n}
                    size='small'
                    onClick={() => setPage(n)}
                    sx={{
                      textTransform: 'none',
                      minWidth: 32,
                      px: 1,
                      color: n === safePage ? '#fff' : COLORS.muted,
                      bgcolor: n === safePage ? COLORS.black : 'transparent',
                      '&:hover': { bgcolor: n === safePage ? '#000' : COLORS.pageBg },
                    }}
                  >
                    {n + 1}
                  </Button>
                ),
              )}
              <Button
                size='small'
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default PublisherInventoryTable;
